
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

export default function Results() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCollectAnimation, setShowCollectAnimation] = useState<string | null>(null);
  const [collectedGems, setCollectedGems] = useState<Set<string>>(new Set());
  const [showCollectAllAnimation, setShowCollectAllAnimation] = useState(false);
  const [expandedWords, setExpandedWords] = useState<Set<number>>(new Set());

  // Generar fechas desde ayer hacia atrás
  const generateHistoricalDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) { // Últimos 30 días
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dates.push({
        value: date.toISOString().split('T')[0],
        label: i === 1 ? 'Ayer' : 
               i === 2 ? 'Anteayer' :
               date.toLocaleDateString('es-ES', { 
                 weekday: 'short', 
                 day: 'numeric', 
                 month: 'short' 
               }),
        fullDate: date.toLocaleDateString('es-ES')
      });
    }
    return dates;
  };

  const availableDates = generateHistoricalDates();

  // Establecer ayer como fecha por defecto
  useEffect(() => {
    if (availableDates.length > 0) {
      setSelectedDate(availableDates[0].value);
    }
  }, []);

  // Mock data para resultados históricos
  const getResultsForDate = (dateValue: string) => {
    const baseResults = {
      words: [
        {
          baseWord: 'OCÉANO',
          myAnswer: 'PROFUNDO',
          likes: 23,
          dislikes: 2,
          superlikes: 5,
          reports: 0,
          totalScore: 23 - 2 + (5 * 3) - 0, // 36
          position: 3,
          gems: 70,
          collected: dateValue !== availableDates[0]?.value, // Solo ayer sin recolectar
          top10: [
            { answer: 'AZUL', likes: 45, dislikes: 1, superlikes: 8, reports: 0, totalScore: 68, gems: 150, player: 'Ana_M' },
            { answer: 'INFINITO', likes: 38, dislikes: 3, superlikes: 6, reports: 1, totalScore: 48, gems: 90, player: 'Carlos_R' },
            { answer: 'PROFUNDO', likes: 23, dislikes: 2, superlikes: 5, reports: 0, totalScore: 36, gems: 70, player: 'Tú' },
            { answer: 'SAL', likes: 19, dislikes: 4, superlikes: 2, reports: 1, totalScore: 16, gems: 60, player: 'Luis_P' },
            { answer: 'OLAS', likes: 17, dislikes: 1, superlikes: 3, reports: 0, totalScore: 25, gems: 50, player: 'María_G' },
            { answer: 'PECES', likes: 15, dislikes: 2, superlikes: 1, reports: 0, totalScore: 16, gems: 40, player: 'Pedro_L' },
            { answer: 'BARCO', likes: 12, dislikes: 3, superlikes: 2, reports: 1, totalScore: 10, gems: 30, player: 'Sofia_K' },
            { answer: 'PLAYA', likes: 10, dislikes: 1, superlikes: 1, reports: 0, totalScore: 12, gems: 20, player: 'Diego_M' },
            { answer: 'AGUA', likes: 8, dislikes: 2, superlikes: 0, reports: 0, totalScore: 6, gems: 10, player: 'Laura_S' },
            { answer: 'MAR', likes: 6, dislikes: 1, superlikes: 1, reports: 0, totalScore: 8, gems: 5, player: 'Javi_T' }
          ]
        },
        {
          baseWord: 'LIBERTAD',
          myAnswer: 'VOLAR',
          likes: 41,
          dislikes: 1,
          superlikes: 7,
          reports: 0,
          totalScore: 61,
          position: 1,
          gems: 150,
          collected: dateValue !== availableDates[0]?.value,
          top10: [
            { answer: 'VOLAR', likes: 41, dislikes: 1, superlikes: 7, reports: 0, totalScore: 61, gems: 150, player: 'Tú' },
            { answer: 'INDEPENDENCIA', likes: 35, dislikes: 2, superlikes: 4, reports: 0, totalScore: 45, gems: 90, player: 'Carmen_R' },
            { answer: 'PAZ', likes: 28, dislikes: 1, superlikes: 3, reports: 1, totalScore: 31, gems: 70, player: 'Roberto_M' },
            { answer: 'DEMOCRACIA', likes: 22, dislikes: 3, superlikes: 2, reports: 0, totalScore: 25, gems: 60, player: 'Elena_P' },
            { answer: 'JUSTICIA', likes: 18, dislikes: 2, superlikes: 1, reports: 0, totalScore: 19, gems: 50, player: 'Miguel_L' },
            { answer: 'DERECHOS', likes: 15, dislikes: 1, superlikes: 2, reports: 0, totalScore: 20, gems: 40, player: 'Lucia_G' },
            { answer: 'ESCAPE', likes: 12, dislikes: 2, superlikes: 1, reports: 1, totalScore: 8, gems: 30, player: 'Andrés_K' },
            { answer: 'AIRE', likes: 9, dislikes: 1, superlikes: 0, reports: 0, totalScore: 8, gems: 20, player: 'Natalia_M' },
            { answer: 'PALOMA', likes: 7, dislikes: 0, superlikes: 1, reports: 0, totalScore: 10, gems: 10, player: 'Fernando_S' },
            { answer: 'LIBRE', likes: 5, dislikes: 1, superlikes: 0, reports: 0, totalScore: 4, gems: 5, player: 'Isabel_T' }
          ]
        },
        {
          baseWord: 'MÚSICA',
          myAnswer: 'MELODÍA',
          likes: 31,
          dislikes: 2,
          superlikes: 4,
          reports: 0,
          totalScore: 41,
          position: 2,
          gems: 90,
          collected: dateValue !== availableDates[0]?.value,
          top10: [
            { answer: 'ARMONÍA', likes: 39, dislikes: 1, superlikes: 6, reports: 0, totalScore: 56, gems: 150, player: 'David_R' },
            { answer: 'MELODÍA', likes: 31, dislikes: 2, superlikes: 4, reports: 0, totalScore: 41, gems: 90, player: 'Tú' },
            { answer: 'RITMO', likes: 26, dislikes: 1, superlikes: 3, reports: 0, totalScore: 34, gems: 70, player: 'Patricia_M' },
            { answer: 'SONIDO', likes: 21, dislikes: 2, superlikes: 2, reports: 1, totalScore: 20, gems: 60, player: 'Raúl_P' },
            { answer: 'GUITARRA', likes: 17, dislikes: 1, superlikes: 1, reports: 0, totalScore: 19, gems: 50, player: 'Cristina_L' },
            { answer: 'PIANO', likes: 14, dislikes: 0, superlikes: 2, reports: 0, totalScore: 20, gems: 40, player: 'Álvaro_G' },
            { answer: 'BAILE', likes: 11, dislikes: 1, superlikes: 1, reports: 0, totalScore: 13, gems: 30, player: 'Mónica_K' },
            { answer: 'CANCIÓN', likes: 8, dislikes: 0, superlikes: 1, reports: 0, totalScore: 11, gems: 20, player: 'Sergio_M' },
            { answer: 'NOTA', likes: 6, dislikes: 1, superlikes: 0, reports: 0, totalScore: 5, gems: 10, player: 'Beatriz_S' },
            { answer: 'CONCIERTO', likes: 4, dislikes: 0, superlikes: 0, reports: 0, totalScore: 4, gems: 5, player: 'Tomás_T' }
          ]
        },
        {
          baseWord: 'AVENTURA',
          myAnswer: 'EXPLORAR',
          likes: 28,
          dislikes: 3,
          superlikes: 3,
          reports: 1,
          totalScore: 33,
          position: 4,
          gems: 60,
          collected: dateValue !== availableDates[0]?.value,
          top10: [
            { answer: 'DESCUBRIR', likes: 42, dislikes: 2, superlikes: 7, reports: 0, totalScore: 61, gems: 150, player: 'Marina_L' },
            { answer: 'EMOCIÓN', likes: 36, dislikes: 1, superlikes: 5, reports: 0, totalScore: 50, gems: 90, player: 'Alejandro_M' },
            { answer: 'VALIENTE', likes: 33, dislikes: 2, superlikes: 4, reports: 0, totalScore: 43, gems: 70, player: 'Carla_P' },
            { answer: 'EXPLORAR', likes: 28, dislikes: 3, superlikes: 3, reports: 1, totalScore: 33, gems: 60, player: 'Tú' },
            { answer: 'RIESGO', likes: 24, dislikes: 1, superlikes: 2, reports: 0, totalScore: 29, gems: 50, player: 'Diego_R' },
            { answer: 'VIAJE', likes: 20, dislikes: 2, superlikes: 1, reports: 0, totalScore: 22, gems: 40, player: 'Sofía_G' },
            { answer: 'MONTAÑA', likes: 16, dislikes: 1, superlikes: 2, reports: 0, totalScore: 21, gems: 30, player: 'Pablo_K' },
            { answer: 'SELVA', likes: 13, dislikes: 0, superlikes: 1, reports: 0, totalScore: 16, gems: 20, player: 'Ana_M' },
            { answer: 'TESORO', likes: 10, dislikes: 1, superlikes: 0, reports: 0, totalScore: 9, gems: 10, player: 'Luis_S' },
            { answer: 'MAPA', likes: 7, dislikes: 0, superlikes: 1, reports: 0, totalScore: 10, gems: 5, player: 'Clara_T' }
          ]
        },
        {
          baseWord: 'SUEÑO',
          myAnswer: 'ESPERANZA',
          likes: 35,
          dislikes: 1,
          superlikes: 6,
          reports: 0,
          totalScore: 52,
          position: 2,
          gems: 90,
          collected: dateValue !== availableDates[0]?.value,
          top10: [
            { answer: 'ILUSIÓN', likes: 44, dislikes: 1, superlikes: 8, reports: 0, totalScore: 67, gems: 150, player: 'Roberto_F' },
            { answer: 'ESPERANZA', likes: 35, dislikes: 1, superlikes: 6, reports: 0, totalScore: 52, gems: 90, player: 'Tú' },
            { answer: 'FANTASÍA', likes: 29, dislikes: 2, superlikes: 4, reports: 0, totalScore: 39, gems: 70, player: 'Elena_V' },
            { answer: 'DESEO', likes: 25, dislikes: 1, superlikes: 3, reports: 0, totalScore: 33, gems: 60, player: 'Miguel_H' },
            { answer: 'META', likes: 21, dislikes: 2, superlikes: 2, reports: 0, totalScore: 25, gems: 50, player: 'Carmen_J' },
            { answer: 'ASPIRACIÓN', likes: 18, dislikes: 1, superlikes: 1, reports: 0, totalScore: 20, gems: 40, player: 'David_N' },
            { answer: 'DORMIR', likes: 14, dislikes: 3, superlikes: 1, reports: 1, totalScore: 9, gems: 30, player: 'Laura_Q' },
            { answer: 'NOCHE', likes: 11, dislikes: 1, superlikes: 0, reports: 0, totalScore: 10, gems: 20, player: 'Andrés_W' },
            { answer: 'CAMA', likes: 8, dislikes: 2, superlikes: 0, reports: 0, totalScore: 6, gems: 10, player: 'Natalia_E' },
            { answer: 'PESADILLA', likes: 5, dislikes: 4, superlikes: 0, reports: 2, totalScore: -1, gems: 5, player: 'Fernando_R' }
          ]
        }
      ]
    };

    // Calcular totales
    const totalGems = baseResults.words.reduce((sum, word) => sum + word.gems, 0);
    const totalVotes = baseResults.words.reduce((sum, word) => sum + word.likes + word.dislikes + word.superlikes + word.reports, 0);
    const averagePosition = baseResults.words.reduce((sum, word) => sum + word.position, 0) / baseResults.words.length;

    return {
      ...baseResults,
      totalGems,
      totalVotes,
      averagePosition: Math.round(averagePosition * 10) / 10
    };
  };

  // Función para verificar si una gema está recolectada
  const isGemCollected = (wordIndex: number) => {
    const gemId = `${selectedDate}-${wordIndex}`;
    return collectedGems.has(gemId) || (currentDayData?.words[wordIndex].collected ?? false);
  };

  const currentDayData = selectedDate ? getResultsForDate(selectedDate) : null;
  const totalPendingGems = currentDayData ? currentDayData.words
    .filter((word, index) => !isGemCollected(index))
    .reduce((sum, word) => sum + word.gems, 0) : 0;

  const handleCollectGem = (wordIndex: number) => {
    const gemId = `${selectedDate}-${wordIndex}`;
    setShowCollectAnimation(gemId);
    
    setTimeout(() => {
      setCollectedGems(prev => new Set([...prev, gemId]));
      setShowCollectAnimation(null);
    }, 1500);
  };

  const handleCollectAll = () => {
    if (totalPendingGems === 0 || !currentDayData) return;
    
    setShowCollectAllAnimation(true);
    
    setTimeout(() => {
      currentDayData.words.forEach((_, index) => {
        const gemId = `${selectedDate}-${index}`;
        setCollectedGems(prev => new Set([...prev, gemId]));
      });
      setShowCollectAllAnimation(false);
    }, 2000);
  };

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return 'ri-trophy-fill';
      case 2: return 'ri-medal-fill';
      case 3: return 'ri-award-fill';
      default: return 'ri-hashtag';
    }
  };

  const getPositionColor = (position: number) => {
    switch (position) {
      case 1: return 'text-yellow-500';
      case 2: return 'text-gray-400';
      case 3: return 'text-amber-600';
      default: return 'text-gray-500';
    }
  };

  const getPositionBg = (position: number) => {
    switch (position) {
      case 1: return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
      case 2: return 'bg-gradient-to-r from-gray-300 to-gray-500';
      case 3: return 'bg-gradient-to-r from-amber-400 to-amber-600';
      default: return 'bg-gradient-to-r from-gray-400 to-gray-600';
    }
  };

  // Función para obtener frase fija basada en índice
  const getFixedPhrase = (index: number) => {
    const motivationalPhrases = [
      "¡Este sí que tiene chispa!",
      "Palabro con aroma a genio.",
      "¿Cómo se te ocurre eso?",
      "Casi te conviertes en maestro de Palabros.",
      "Ups… pero nos reímos igual.",
      "Vaya combinación de neuronas."
    ];
    return motivationalPhrases[index % motivationalPhrases.length];
  };

  const selectedDateInfo = availableDates.find(d => d.value === selectedDate);

  const toggleWordExpansion = (wordIndex: number) => {
    setExpandedWords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(wordIndex)) {
        newSet.delete(wordIndex);
      } else {
        newSet.add(wordIndex);
      }
      return newSet;
    });
  };

  const isWordExpanded = (wordIndex: number) => {
    return expandedWords.has(wordIndex);
  };

  // Si no hay datos o fecha seleccionada, mostrar loading
  if (!currentDayData || !selectedDateInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 pb-40 pt-16">
      {/* Barra superior fija - igual que en la pantalla de jugar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="flex items-center justify-between px-6 py-3">
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>
            PALABROS
          </h1>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/gems')}
              className="flex items-center space-x-2 bg-gradient-to-r from-yellow-400 to-amber-500 px-3 py-1.5 rounded-full hover:from-yellow-500 hover:to-amber-600 transition-all shadow-lg"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-white text-sm"></i>
              </div>
              <span className="text-white font-bold text-sm">1,247</span>
            </button>
          </div>
        </div>
      </div>

      {/* Header de resultados con fondo degradado */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 pt-8 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Resultados</h1>
            <p className="text-white/80 text-sm">
              ¡Mientras menos, más original!
            </p>
          </div>
        </div>

        {/* Selector de fecha */}
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="w-full bg-white/20 backdrop-blur-sm rounded-lg px-4 py-3 text-white font-medium flex items-center justify-between hover:bg-white/30 transition-all"
          >
            <div className="text-left">
              <div className="font-bold">{selectedDateInfo.label}</div>
              <div className="text-xs opacity-80">{selectedDateInfo.fullDate}</div>
            </div>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${showDatePicker ? 'rotate-180' : ''}`}></i>
            </div>
          </button>

          {/* Dropdown de fechas */}
          {showDatePicker && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto z-50">
              {availableDates.map((date) => (
                <button
                  key={date.value}
                  onClick={() => {
                    setSelectedDate(date.value);
                    setShowDatePicker(false);
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    selectedDate === date.value ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700'
                  }`}
                >
                  <div className="font-medium">{date.label}</div>
                  <div className="text-xs text-gray-500">{date.fullDate}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Resumen del día */}
        <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-white flex items-center justify-center space-x-1">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-yellow-300 text-lg"></i>
                </div>
                <span>{currentDayData.totalGems}</span>
              </div>
              <div className="text-white/80 text-xs">Gemas totales</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white flex items-center justify-center space-x-1">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-thumb-up-fill text-blue-300 text-lg"></i>
                </div>
                <span>{currentDayData.totalVotes}</span>
              </div>
              <div className="text-white/80 text-xs">Votos recibidos</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                #{currentDayData.averagePosition}
              </div>
              <div className="text-white/80 text-xs">Posición media</div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido - Acordeones */}
      <div className="px-4 mt-4 space-y-3 pb-6">
        {currentDayData.words.map((wordData, wordIndex) => (
          <div key={wordIndex} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header del acordeón - siempre visible */}
            <button
              onClick={() => toggleWordExpansion(wordIndex)}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-4 hover:from-indigo-600 hover:to-purple-700 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-lg font-bold text-white">{wordData.baseWord}</h3>
                  <p className="text-white/80 text-sm">Tu respuesta: {wordData.myAnswer}</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-bold ${getPositionBg(wordData.position)} text-white`}>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${getPositionIcon(wordData.position)} text-xs`}></i>
                      </div>
                      <span>#{wordData.position}</span>
                    </div>
                    <div className="text-white/80 text-xs mt-1">+{wordData.gems} gemas</div>
                  </div>
                  
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-white text-lg transition-transform duration-300 ${
                      isWordExpanded(wordIndex) ? 'rotate-180' : ''
                    }`}></i>
                  </div>
                </div>
              </div>
            </button>

            {/* Contenido expandible */}
            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isWordExpanded(wordIndex) ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}>
              {/* Tu resultado */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full ${getPositionBg(wordData.position)} flex items-center justify-center`}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${getPositionIcon(wordData.position)} text-white text-sm`}></i>
                      </div>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Tu resultado</p>
                      <p className="text-sm text-gray-600">
                        Puntuación: {wordData.totalScore} • +{wordData.gems} gemas
                      </p>
                    </div>
                  </div>

                  {!isGemCollected(wordIndex) ? (
                    <button
                      onClick={() => handleCollectGem(wordIndex)}
                      className="relative bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-lg font-bold text-xs hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg overflow-hidden"
                    >
                      {showCollectAnimation === `${selectedDate}-${wordIndex}` && (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 animate-pulse"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        </>
                      )}
                      <span className={showCollectAnimation === `${selectedDate}-${wordIndex}` ? 'opacity-0' : ''}>
                        Reclamar
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-1 text-green-600 text-xs font-bold">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line text-xs"></i>
                      </div>
                      <span>Reclamado</span>
                    </div>
                  )}
                </div>

                {/* Desglose de votos */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center bg-white rounded-lg p-2">
                    <div className="w-4 h-4 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-heart-fill text-green-500 text-sm"></i>
                    </div>
                    <div className="text-xs font-bold text-gray-700">{wordData.likes}</div>
                    <div className="text-xs text-gray-500">Likes</div>
                  </div>
                  <div className="text-center bg-white rounded-lg p-2">
                    <div className="w-4 h-4 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-dislike-fill text-red-500 text-sm"></i>
                    </div>
                    <div className="text-xs font-bold text-gray-700">{wordData.dislikes}</div>
                    <div className="text-xs text-gray-500">Dislikes</div>
                  </div>
                  <div className="text-center bg-white rounded-lg p-2">
                    <div className="w-4 h-4 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                    </div>
                    <div className="text-xs font-bold text-gray-700">{wordData.superlikes}</div>
                    <div className="text-xs text-gray-500">Superlikes</div>
                  </div>
                  <div className="text-center bg-white rounded-lg p-2">
                    <div className="w-4 h-4 flex items-center justify-center mx-auto mb-1">
                      <i className="ri-flag-fill text-purple-500 text-sm"></i>
                    </div>
                    <div className="text-xs font-bold text-gray-700">{wordData.reports}</div>
                    <div className="text-xs text-gray-500">Reports</div>
                  </div>
                </div>
              </div>

              {/* Top 10 */}
              <div className="p-4">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-trophy-line text-yellow-500 text-lg"></i>
                  </div>
                  Top 10 Palabros
                </h4>
                
                <div className="space-y-2">
                  {wordData.top10.map((entry, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        entry.player === 'Tú'
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 rounded-full ${getPositionBg(index + 1)} flex items-center justify-center`}>
                          <span className="text-white text-xs font-bold">{index + 1}</span>
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${entry.player === 'Tú' ? 'text-blue-900' : 'text-gray-900'}`}>
                            {entry.answer}
                          </p>
                          <p className="text-xs text-gray-600 italic">
                            {getFixedPhrase(index)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center space-x-1 text-xs text-gray-600 mb-1">
                          <span>Puntuación: {entry.totalScore}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-xs font-bold text-yellow-600">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-xs"></i>
                          </div>
                          <span>{entry.gems}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Botón Reclamar Todas al final */}
      {totalPendingGems > 0 && (
        <div className="fixed bottom-24 left-4 right-4 z-40">
          <button
            onClick={handleCollectAll}
            className="relative w-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white py-4 rounded-xl font-bold text-lg hover:from-yellow-500 hover:to-amber-600 transition-all shadow-xl flex items-center justify-center space-x-3 overflow-hidden"
          >
            {showCollectAllAnimation && (
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-amber-400 animate-pulse"></div>
            )}
            <div className="relative flex items-center space-x-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-star-fill text-white text-xl"></i>
              </div>
              <span>Reclamar todas las gemas ({totalPendingGems})</span>
            </div>
            
            {showCollectAllAnimation && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </button>
        </div>
      )}

      {/* Animación de gemas flotantes */}
      {(showCollectAnimation || showCollectAllAnimation) && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`,
                animationDelay: `${i * 0.1}s`,
                animationDuration: '1.5s'
              }}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-star-fill text-yellow-400 text-2xl drop-shadow-lg"></i>
              </div>
            </div>
          ))}
        </div>
      )}

      <Navigation />
    </div>
  );
}
